import React from "react";
import { Box, Button, Checkbox, Flex, Radio, RadioGroup, Text, useTranslation, Icon, Link, GridItem } from "@familyzone/component-library";
import { Grid } from "@chakra-ui/react";
import { SectionHeading } from "../../shared/SectionHeading";
import { TabTypes } from ".";

interface ImageSettingsTabProps {
  saving: boolean;
  disabled: boolean;
  onSave: (tabType: TabTypes) => void;
  onReset: () => void;
  resetSettingsDisabled: boolean;

  goreDetectionEnabled: boolean;
  setGoreDetectionEnabled: (val: boolean) => void;
  goreDetectionSensitivity: number;
  setGoreDetectionSensitivity: (val: number) => void;
  pornDetectionEnabled: boolean;
  setPornDetectionEnabled: (val: boolean) => void;
  pornDetectionSensitivity: number;
  setPornDetectionSensitivity: (val: number) => void;
  intimateApparelDetectionEnabled: boolean;
  setIntimateApparelDetectionEnabled: (val: boolean) => void;
  intimateApparelDetectionSensitivity: number;
  setIntimateApparelDetectionSensitivity: (val: number) => void;
}

const ImageSettingsTab = ({
  saving,
  disabled,
  onSave,
  onReset,
  resetSettingsDisabled,

  goreDetectionEnabled,
  setGoreDetectionEnabled,
  goreDetectionSensitivity,
  setGoreDetectionSensitivity,
  pornDetectionEnabled,
  setPornDetectionEnabled,
  pornDetectionSensitivity,
  setPornDetectionSensitivity,
  intimateApparelDetectionEnabled,
  setIntimateApparelDetectionEnabled,
  intimateApparelDetectionSensitivity,
  setIntimateApparelDetectionSensitivity,
}: ImageSettingsTabProps): JSX.Element => {
  const { t } = useTranslation();

  return (
    <Flex direction="column" minH="100%">
      <Box backgroundColor="white" borderRadius="2xl" boxShadow="sm" pt="sp24">
        <Box px="sp24">
          <SectionHeading title={t("Categories")} aria-level={2} />

          <Grid mt="sp36" templateColumns="repeat(2, 1fr)" columnGap="sp4">
            <GridItem colSpan={1} mt="sp24" ml="sp12" mr="sp12">
              <Text color="neutrals.120">{t("Set the content categories and detection sensitivity for Content Aware.")}</Text>
              <Grid templateColumns="20px 1fr" columnGap="sp8" alignItems="start" mt="sp16">
                <Checkbox
                  isChecked={goreDetectionEnabled}
                  isDisabled={disabled}
                  onChange={(e) => setGoreDetectionEnabled(e.target.checked)}
                  aria-labelledby="gore-detection-label"
                />
                <Box>
                  <Text id="gore-detection-label" fontWeight="medium" mt="-sp2">
                    {t("Gore detection")}
                  </Text>
                  {goreDetectionEnabled && (
                    <Flex mt="sp8" alignItems="center" flexWrap="wrap">
                      <Text mr="sp8" color={"neutrals.600"}>
                        {t("Sensitivity level:")}
                      </Text>
                      <RadioGroup
                        direction="row"
                        value={goreDetectionSensitivity}
                        onChange={(v: string) => setGoreDetectionSensitivity(Number(v))}
                      >
                        <Radio value={0} isDisabled={disabled}>
                          {t("Low")}
                        </Radio>
                        <Radio value={0.5} isDisabled={disabled}>
                          {t("Medium")}
                        </Radio>
                        <Radio value={1} isDisabled={disabled}>
                          {t("High")}
                        </Radio>
                      </RadioGroup>
                    </Flex>
                  )}
                </Box>
              </Grid>

              <Grid templateColumns="20px 1fr" columnGap="sp8" alignItems="start" mt="sp16">
                <Checkbox
                  isChecked={pornDetectionEnabled}
                  isDisabled={disabled}
                  onChange={(e) => setPornDetectionEnabled(e.target.checked)}
                  aria-labelledby="porn-detection-label"
                />
                <Box>
                  <Text id="porn-detection-label" fontWeight="medium" mt="-sp2">
                    {t("Pornography detection")}
                  </Text>
                  {pornDetectionEnabled && (
                    <Flex mt="sp8" alignItems="center" flexWrap="wrap">
                      <Text mr="sp8" color={"neutrals.600"}>
                        {t("Sensitivity level:")}
                      </Text>
                      <RadioGroup
                        direction="row"
                        value={pornDetectionSensitivity}
                        onChange={(v: string) => setPornDetectionSensitivity(Number(v))}
                      >
                        <Radio value={0} isDisabled={disabled}>
                          {t("Low")}
                        </Radio>
                        <Radio value={0.5} isDisabled={disabled}>
                          {t("Medium")}
                        </Radio>
                        <Radio value={1} isDisabled={disabled}>
                          {t("High")}
                        </Radio>
                      </RadioGroup>
                    </Flex>
                  )}
                </Box>
              </Grid>

              <Grid templateColumns="20px 1fr" columnGap="sp8" alignItems="start" mt="sp16">
                <Checkbox
                  isChecked={intimateApparelDetectionEnabled}
                  isDisabled={disabled}
                  onChange={(e) => setIntimateApparelDetectionEnabled(e.target.checked)}
                  aria-labelledby="intimate-detection-label"
                />
                <Box>
                  <Text id="intimate-detection-label" fontWeight="medium" mt="-sp2">
                    {t("Intimate Apparel detection")}
                  </Text>

                  {intimateApparelDetectionEnabled && (
                    <Flex mt="sp8" alignItems="center" flexWrap="wrap">
                      <Text mr="sp8" color={"neutrals.600"}>
                        {t("Sensitivity level:")}
                      </Text>
                      <RadioGroup
                        direction="row"
                        value={intimateApparelDetectionSensitivity}
                        onChange={(v: string) => setIntimateApparelDetectionSensitivity(Number(v))}
                      >
                        <Radio value={0} isDisabled={disabled}>
                          {t("Low")}
                        </Radio>
                        <Radio value={0.5} isDisabled={disabled}>
                          {t("Medium")}
                        </Radio>
                        <Radio value={1} isDisabled={disabled}>
                          {t("High")}
                        </Radio>
                      </RadioGroup>
                    </Flex>
                  )}
                </Box>
              </Grid>
            </GridItem>

            <GridItem colSpan={1} mt="sp24" mr="sp12">
              <Box backgroundColor="#E5F9F6" borderRadius="md" px="sp16" py="sp8" maxW="520px">
                <Flex alignItems="center" mb="sp12" gap="sp12">
                  <Icon icon="fa-info-circle" color="teal" />
                  <Text fontWeight="medium">{t("Sensitivity levels explained")}</Text>
                </Flex>

                <Grid templateColumns="80px 1fr" rowGap="sp8" columnGap="sp12">
                  <Text pl="sp24" color="neutrals.700" fontSize="13.5px">
                    {t("Low")}
                  </Text>
                  <Text color="neutrals.700" fontSize="13.5px">
                    {t("Blurs content when detection confidence is high, less false positives.")}
                  </Text>

                  <Text pl="sp24" color="neutrals.700" fontSize="13.5px">
                    {t("Medium")}
                  </Text>
                  <Text color="neutrals.700" fontSize="13.5px">
                    {t("Blurs content at a moderate detection confidence, moderate chance of false positives.")}
                  </Text>

                  <Text pl="sp24" color="neutrals.700" fontSize="13.5px">
                    {t("High")}
                  </Text>
                  <Text color="neutrals.700" fontSize="13.5px">
                    {t("Blurs content with low detection confidence, higher chance of false positives.")}
                  </Text>
                </Grid>

                <Text mt="sp8" fontStyle="italic" color="neutrals.700" pl="sp24" fontSize="13.5px">
                  {t("For more information about Content Aware settings please")}{" "}
                  <Link
                    textDecoration="underline"
                    href="https://help.linewize.com/hc/en-gb/articles/21788694224412"
                    isExternal={true}
                    style={{ color: "#1772ba" }}
                  >
                    read our knowledge article.
                  </Link>
                </Text>
              </Box>
            </GridItem>
          </Grid>
        </Box>

        <Flex mt="sp24" backgroundColor="#F5FCFF" p="sp24" borderBottomLeftRadius="2xl" borderBottomRightRadius="2xl">
          <Button
            variant="primary"
            disabled={disabled}
            onClick={() => onSave(TabTypes.IMAGE)}
            data-testid="save-image-content-aware-config-button"
            aria-label={saving ? t("Saving...") : t("Save Changes")}
          >
            {saving ? (
              <div style={{ width: "90px", margin: "1px 0" }}>{t("Saving...")}</div>
            ) : (
              <div style={{ width: "90px", margin: "1px 0" }}>{t("Save Changes")}</div>
            )}
          </Button>
          <Button
            ml="sp12"
            variant={resetSettingsDisabled ? undefined : "outline"}
            data-testid="reset-image-content-aware-config-button"
            disabled={disabled || resetSettingsDisabled}
            onClick={onReset}
          >
            {t("Reset Image Settings")}
          </Button>
        </Flex>
      </Box>
    </Flex>
  );
};

export default ImageSettingsTab;
